import { Component, inject } from '@angular/core';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacDialogLayoutComponent,
  CacFieldComponent,
  CacSwitchComponent,
} from '@controladad/ng-base';
import { formControl, formGroup, Validators } from '@al00x/forms';
import { CategoryApiService, CouponApiService, UserApiService, AdminProductsApiService } from '../../../services';
import { CouponEntity, TargetType, CouponTarget, ProductDropdownItem, UserEntity } from '@models';
import { CouponTypeValues, DiscountForValues } from '../../../data';
import { ReactiveFormsModule } from '@angular/forms';
import { LazyDropdownComponent, LazyDropdownItem } from '../../../components';

export type CouponDialogData = { coupon?: CouponEntity };
export type CouponDialogResult = boolean;


@Component({
  selector: 'lib-coupon-dialog',
  standalone: true,
  imports: [CacDialogLayoutComponent, CacFieldComponent, CacSwitchComponent, ReactiveFormsModule, LazyDropdownComponent],
  templateUrl: './coupon-dialog.component.html',
  styleUrls: ['./coupon-dialog.component.scss'],
})
export class CouponDialogComponent extends CacBaseDialogComponent<CouponDialogData, CouponDialogResult> {
  private readonly couponApi = inject(CouponApiService);
  private readonly categoryApi = inject(CategoryApiService);
  private readonly userApi = inject(UserApiService);
  private readonly productApi = inject(AdminProductsApiService);

  // Expose TargetType enum for template use
  readonly TargetType = TargetType;

  // Helper methods to convert between targets and form structure
  private targetsToFormData(targets: CouponTarget[]) {
    const discountFor: TargetType[] = [];
    const productIds: number[] = [];
    const categoryIds: number[] = [];
    const customerIds: number[] = [];
    const vendorIds: number[] = [];

    targets.forEach(target => {
      discountFor.push(target.targetType);

      switch (target.targetType) {
        case TargetType.Product:
          productIds.push(...target.targetIds);
          break;
        case TargetType.Category:
          categoryIds.push(...target.targetIds);
          break;
        case TargetType.Customer:
          customerIds.push(...target.targetIds);
          break;
        case TargetType.Vendor:
          vendorIds.push(...target.targetIds);
          break;
      }
    });

    return { discountFor, productIds, categoryIds, customerIds, vendorIds };
  }

  private formDataToTargets(formData: any): CouponTarget[] {
    const targets: CouponTarget[] = [];

    if (formData.discountFor) {
      formData.discountFor.forEach((targetType: TargetType) => {
        let targetIds: number[] = [];

        switch (targetType) {
          case TargetType.Product:
            targetIds = formData.productIds || [];
            break;
          case TargetType.Category:
            targetIds = formData.categoryIds || [];
            break;
          case TargetType.Customer:
            targetIds = formData.customerIds || [];
            break;
          case TargetType.Vendor:
            targetIds = formData.vendorIds || [];
            break;
          case TargetType.CartItem:
            targetIds = [1];//Should not be empty
            break;
        }

        targets.push({
          targetType: targetType,
          targetIds: targetIds
        });
      });
    }

    return targets;
  }

  form = formGroup({
    name: formControl<string>('', Validators.required),
    code: formControl<string>('', Validators.required),
    expirationDate: formControl<string | null>(null),
    usageLimitation: formControl<number | null>(undefined, [Validators.min(0)]),
    discountFor: formControl<TargetType[]>([], Validators.required),
    productIds: formControl<number[]>([]),
    vendorIds: formControl<number[]>([]),
    categoryIds: formControl<number[]>([]),
    customerIds: formControl<number[]>([]),
    type: formControl<number>(0, Validators.required),
    discountAmount: formControl<number | undefined>(undefined, [Validators.required, Validators.min(0)]),
    hasShippingDiscount: formControl<boolean>(false),
    shippingDiscountAmount: formControl<number | null>(null, [Validators.min(0)]),
    applyOnDiscountedProducts: formControl<boolean>(false),
  });

  // Observable items for dropdowns
  discountForItems = DiscountForValues;
  couponTypeItems = CouponTypeValues;
  productItems$ = this.couponApi.getProductItems();
  vendorItems$ = this.couponApi.getVendorItems();
  categoryItems$ = this.categoryApi.getFlatCategories();

  // Customer dropdown configuration
  customerApiAdapter = this.userApi.getCustomerAdapter();
  customerItemMapper = (user: UserEntity): LazyDropdownItem => ({
    id: user.id,
    label: `${user.id} - ${user.email || user.phoneNumber || 'No contact'}`.trim(),
    value: user.id,
    subtitle: user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ')
  });

  // Product dropdown configuration
  productApiAdapter = this.productApi.getProductNamesAdapter();
  productItemMapper = (product: ProductDropdownItem): LazyDropdownItem => ({
    id: product.id,
    label: product.name.english || product.name.arabic || 'Unnamed Product',
    value: product.id,
    subtitle: product.name.arabic && product.name.english !== product.name.arabic ? product.name.arabic : undefined,
    image: product.productFirstImage
  });

  // Helper method for field visibility based on target type
  shouldShowTargetField(targetType: TargetType): boolean {
    return this.form.controls.discountFor.value?.includes(targetType) || false;
  }

  shouldShowDiscountFields(): boolean {
    return (this.form.controls.discountFor.value?.length || 0) > 0;
  }

  shouldShowShippingDiscountAmount(): boolean {
    return this.form.controls.hasShippingDiscount.value || false;
  }

  onProductSelectionChange(selectedProducts: LazyDropdownItem | LazyDropdownItem[] | null) {
    let productIds: number[] = [];

    if (selectedProducts) {
      if (Array.isArray(selectedProducts)) {
        // Multiple selection - array of products
        productIds = selectedProducts.map(product => product.id);
      } else {
        // Single selection - single product
        productIds = [selectedProducts.id];
      }
    }

    this.form.controls.productIds.setValue(productIds);
  }

  onCustomerSelectionChange(selectedCustomers: LazyDropdownItem | LazyDropdownItem[] | null) {
    let customerIds: number[] = [];

    if (selectedCustomers) {
      if (Array.isArray(selectedCustomers)) {
        const existingIds = this.form.controls.customerIds.value || [];
        customerIds = [...existingIds, ...selectedCustomers.map(customer => customer.id)];
      } else {
        const existingIds = this.form.controls.customerIds.value || [];
        customerIds = [...existingIds, selectedCustomers.id];
      }
    }

    this.form.controls.customerIds.setValue(customerIds);
  }

  // Handle shipping discount field validation
  private updateShippingDiscountValidation(hasShippingDiscount: boolean) {
    const shippingDiscountControl = this.form.controls.shippingDiscountAmount;

    if (hasShippingDiscount) {
      shippingDiscountControl.addValidators(Validators.required);
    } else {
      shippingDiscountControl.removeValidators(Validators.required);
      shippingDiscountControl.setValue(null);
    }

    shippingDiscountControl.updateValueAndValidity();
  }

  // Handle discount amount validation based on type
  private updateDiscountAmountValidation(type: number) {
    const discountAmountControl = this.form.controls.discountAmount;

    if (type === 1) { // Percent
      discountAmountControl.addValidators(Validators.max(100));
    } else {
      discountAmountControl.removeValidators(Validators.max(100));
    }

    discountAmountControl.updateValueAndValidity();
  }


  constructor() {
    super();

    // Set up form subscriptions
    this.form.controls.hasShippingDiscount.valueChanges.subscribe(hasShippingDiscount => {
      this.updateShippingDiscountValidation(hasShippingDiscount);
    });

    this.form.controls.type.valueChanges.subscribe(type => {
      this.updateDiscountAmountValidation(type);
    });

    // Initialize form with existing data if editing
    const values = this.data.coupon;
    if (values) {
      const formData = this.targetsToFormData(values.targets);
      const hasShippingDiscount = values.shippingDiscountAmount != null && values.shippingDiscountAmount > 0;

      this.form.patchValue({
        name: values.name,
        code: values.code,
        expirationDate: values.expirationDate,
        type: values.type,
        discountFor: formData.discountFor,
        productIds: formData.productIds,
        vendorIds: formData.vendorIds,
        categoryIds: formData.categoryIds,
        customerIds: formData.customerIds,
        discountAmount: values.discountAmount,
        hasShippingDiscount: hasShippingDiscount,
        shippingDiscountAmount: values.shippingDiscountAmount,
        usageLimitation: values.usageLimitation,
        applyOnDiscountedProducts: values.applyOnDiscountedProducts || false,
      });
    }

    // Set initial validations
    this.updateShippingDiscountValidation(this.form.controls.hasShippingDiscount.value);
    this.updateDiscountAmountValidation(this.form.controls.type.value);
  }

  onSubmit(e: ButtonClickEvent) {
    const value = this.form.value;
    if (!value) return;

    const coupon = this.data.coupon;

    const fields = {
      name: value.name!,
      code: value.code!,
      expirationDate: value.expirationDate!,
      type: value.type!,
      discountAmount: value.discountAmount,
      shippingDiscountAmount: value.hasShippingDiscount ? value.shippingDiscountAmount : null,
      usageLimitation: value.usageLimitation ? value.usageLimitation : null,
      applyOnDiscountedProducts: value.applyOnDiscountedProducts || false,
      targets: this.formDataToTargets(value)
    };

    const request = coupon
      ? this.couponApi.update({ couponId: coupon.id, fields: fields })
      : this.couponApi.create({ fields });

    return request.pipe(e.pipe()).subscribe(() => this.submit(true));
  }
}
